import api from "@/app/api";
import type {
  FeatureFlagKey,
  FeatureFlags,
  UpdateFeatureFlagsRequest,
} from "@/app/api/types/featureFlags";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";

/**
 * Cache tuning shared by every feature-flag query.
 *
 * Flags rarely change, so we cache aggressively; a 15-minute staleTime is the
 * upper bound on how long a super-admin flag/config edit takes to surface
 * (plus remount), in lieu of realtime propagation.
 */
const FLAGS_STALE_TIME = 15 * 60 * 1000; // 15 minutes
const FLAGS_GC_TIME = 60 * 60 * 1000; // 1 hour

/**
 * Query keys for feature flags
 */
export const featureFlagKeys = {
  all: ["feature-flags"] as const,
  my: () => [...featureFlagKeys.all, "my"] as const,
  company: () => [...featureFlagKeys.all, "company"] as const,
  user: () => [...featureFlagKeys.all, "user"] as const,
} as const;

/**
 * THE MASTER FEATURE FLAGS QUERY
 *
 * Returns the current user's EFFECTIVE flags — defaults + company flags + the
 * user's own overrides, merged server-side by `/feature-flags/me`. That
 * endpoint reads fresh from the DB (flags are not in the JWT), so a just-saved
 * override surfaces on the next refetch — no token refresh needed. Use the
 * combined endpoint (not company-only) so user-scope overrides actually gate
 * the UI; reading company flags alone silently ignored them.
 *
 * Use this hook in components to check feature access.
 *
 * @param options.enabled - Gate the query. Defaults to true. Pass `false` on
 *   public surfaces (e.g. the `/` dispatcher for logged-out visitors) to avoid
 *   firing an unauthenticated request that would 401.
 */
export function useFeatureFlags(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: featureFlagKeys.my(),
    queryFn: async () => {
      const response = await api.featureFlags.getMyFeatureFlags();
      return response.flags;
    },
    enabled: options?.enabled ?? true,
    staleTime: FLAGS_STALE_TIME,
    gcTime: FLAGS_GC_TIME,
    refetchOnWindowFocus: false, // No need — flags are near-static
    // Always revalidate on mount (i.e. every page load), even though the
    // cached value is still within `staleTime`. Feature flags are persisted to
    // IndexedDB (see PERSIST_ALLOW_LIST) with a throttled 5s write; reloading
    // right after changing a flag (e.g. the default landing page) would
    // otherwise restore the pre-change value and — being "fresh" under the
    // 15min staleTime — never refetch, so the change appears to revert. A
    // background refetch on mount serves the persisted value instantly and
    // reconciles it with the server, which is the source of truth.
    refetchOnMount: "always",
  });
}

/**
 * Convenience hook to check a single feature flag
 *
 * @param flag - Feature flag name to check
 * @returns The value of the feature flag, undefined if not set
 *
 * @example
 * const hasAS400 = useFeatureFlag('as400Export');
 * const printTemplate = useFeatureFlag('printingConfig') ?? 'default';
 * const customFields = useFeatureFlag('stockCustomFields');
 */
export function useFeatureFlag<K extends FeatureFlagKey>(
  flag: K,
): FeatureFlags[K] {
  const { data: flags = {} } = useFeatureFlags();
  return flags[flag];
}

/**
 * Get company-level feature flags (not merged with user flags)
 */
export function useCompanyFeatureFlags() {
  return useQuery({
    queryKey: featureFlagKeys.company(),
    queryFn: async () => {
      const response = await api.featureFlags.getCompanyFeatureFlags();
      return response.flags;
    },
    staleTime: FLAGS_STALE_TIME,
    gcTime: FLAGS_GC_TIME,
    // Persisted to IndexedDB like the "my" query — see useFeatureFlags for why
    // a mount-time revalidation is required to avoid serving a stale flag edit.
    refetchOnMount: "always",
  });
}

/**
 * Get user-specific feature flags (overrides only, not merged)
 */
export function useUserFeatureFlags() {
  return useQuery({
    queryKey: featureFlagKeys.user(),
    queryFn: async () => {
      const response = await api.featureFlags.getUserFeatureFlags();
      return response.flags;
    },
    staleTime: FLAGS_STALE_TIME,
    gcTime: FLAGS_GC_TIME,
    // Persisted to IndexedDB like the "my" query — see useFeatureFlags for why
    // a mount-time revalidation is required to avoid serving a stale flag edit.
    refetchOnMount: "always",
  });
}

/**
 * Mutation to update company feature flags (admin only)
 *
 * Invalidates all feature flag queries on success.
 */
export function useUpdateCompanyFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateFeatureFlagsRequest) =>
      api.featureFlags.updateCompanyFeatureFlags(request),
    onSuccess: (data) => {
      // Invalidate all feature flag queries to force refetch
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });

      // Update company flags cache immediately
      queryClient.setQueryData(featureFlagKeys.company(), data.flags);

      // Also refetch to ensure consistency
      queryClient.refetchQueries({ queryKey: featureFlagKeys.company() });
    },
  });
}

/**
 * Mutation to update user feature flags
 *
 * Invalidates all feature flag queries on success.
 */
export function useUpdateUserFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      request,
    }: {
      userId: UUID;
      request: UpdateFeatureFlagsRequest;
    }) => api.featureFlags.updateUserFeatureFlags(userId, request),
    onSuccess: (data) => {
      // Invalidate all feature flag queries
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });

      // Update user flags cache optimistically
      queryClient.setQueryData(featureFlagKeys.user(), data.flags);
    },
  });
}

/**
 * Mutation to reset company feature flags to defaults (admin only)
 */
export function useResetCompanyFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.featureFlags.resetCompanyFeatureFlags(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });
      queryClient.setQueryData(featureFlagKeys.company(), data.flags);
    },
  });
}

/**
 * Mutation to reset user feature flags (will inherit from company)
 */
export function useResetUserFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: UUID) =>
      api.featureFlags.resetUserFeatureFlags(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });
      queryClient.setQueryData(featureFlagKeys.user(), data.flags);
    },
  });
}

/**
 * Hook to manually refresh feature flags
 *
 * Use this when you need to force a refresh of feature flags,
 * for example after updating them in the database.
 *
 * @example
 * const refreshFlags = useRefreshFeatureFlags();
 * await refreshFlags();
 */
export function useRefreshFeatureFlags() {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: featureFlagKeys.all });
    await queryClient.refetchQueries({ queryKey: featureFlagKeys.my() });
  };
}

/**
 * Helper function to prefetch feature flags
 *
 * Call this early in your app (e.g., after login) to populate cache.
 *
 * @example
 * await prefetchFeatureFlags(queryClient);
 */
export async function prefetchFeatureFlags(queryClient: any) {
  await queryClient.prefetchQuery({
    queryKey: featureFlagKeys.my(),
    queryFn: async () => {
      const response = await api.featureFlags.getMyFeatureFlags();
      return response.flags;
    },
    staleTime: FLAGS_STALE_TIME,
  });
}
